import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { usePreferences } from '../shell/preferencesContext';
import { Banner, Sidebar, SidebarGroup, SidebarItem } from '../ui';
import { CallsPage } from './CallsPage';
import { ClientsPage } from './ClientsPage';
import { DemandPage } from './DemandPage';
import { DirectoryPage } from './DirectoryPage';
import { JourneysPage } from './JourneysPage';
import { UnplacedPage } from './UnplacedPage';
import { useFrame, type ReportPageProps } from './frame';
import { ServicePage } from './ServicePage';
import { ShiftBookPage } from './ShiftBookPage';

/**
 * Reports as an area beside settings, not a tab inside them.
 *
 * A dispatcher who may read the shift book has no business on the tariff page, and the two
 * are different jobs done at different times — one is somebody settling up at the end of a
 * month, the other is somebody changing how the firm works. A sidebar of four reports says
 * that; a tab called "Reports" among nine others did not.
 *
 * The area holds the period and nothing else about a report. Each page fetches its own
 * answer when it is opened: four reports loading on arrival would be four queries over
 * three months of a whole fleet for the one somebody actually wanted.
 *
 * The call archive is listed only where the server says it is this person's — an
 * administrator at a firm with telephony. A firm without it has no calls, and a page that
 * could only ever say so is not worth a line in the sidebar.
 */
const REPORTS = ['shift-book', 'demand', 'service', 'clients', 'directory', 'journeys', 'addresses', 'calls'] as const;

export type ReportName = (typeof REPORTS)[number];

const PAGES: Record<ReportName, ComponentType<ReportPageProps>> = {
  'shift-book': ShiftBookPage,
  demand: DemandPage,
  service: ServicePage,
  clients: ClientsPage,
  directory: DirectoryPage,
  journeys: JourneysPage,
  addresses: UnplacedPage,
  calls: CallsPage,
};

export function ReportsArea() {
  const { t } = useTranslation();
  const { report } = useParams<{ report: string }>();
  const { access } = usePreferences();

  const offered = REPORTS.filter((name) => name !== 'calls' || access.calls);

  const current = (offered as readonly string[]).includes(report ?? '')
    ? (report as ReportName)
    : 'shift-book';

  const { frame, problem } = useFrame(current);

  const Page = PAGES[current];

  return (
    <div className="flex flex-1 min-h-0">
      <Sidebar>
        <SidebarGroup label={t('reports.title')}>
          {offered.map((name) => (
            <SidebarItem key={name} to={`/reports/${name}`}>
              {t(`nav.reports.${name}`)}
            </SidebarItem>
          ))}
        </SidebarGroup>
      </Sidebar>

      {/* The page scrolls vertically and never sideways: a table wider than the column
          scrolls inside its own card, which is what keeps the period row and the tiles
          where somebody left them. */}
      <div className="flex-1 min-w-0 overflow-y-auto">
        {/* At least the column's height, so something docked at the foot of a page — the call
            archive's player — sits at the bottom of the window however short the list. */}
        <div className="flex flex-col gap-4 px-8 py-6 min-h-full">
          {problem !== null && <Banner tone="danger">{problem}</Banner>}

          <Page frame={frame} />
        </div>
      </div>
    </div>
  );
}
